import type { LeagueCompetitionEventDisplay } from "@/models/eventDisplay";
import {
  competitionDayOffset,
  orderByCompetitionRelevance,
} from "@/features/competitions/competitionDateOrdering";

const SCHEDULE_ROW_LIMIT = 3;
const GPS_ROW_LIMIT = 3;

export interface HomeNextUpCompetition {
  display: LeagueCompetitionEventDisplay;
  /** Whole calendar days until the event in its own timezone (0 = today). */
  dayOffset: number;
}

export interface HomeCompetitionRows {
  /** The published competition running today or next, or null when nothing is upcoming. */
  nextUp: HomeNextUpCompetition | null;
  /** Published competitions after `nextUp`: upcoming first (soonest), then recent history. */
  schedule: LeagueCompetitionEventDisplay[];
  gpsAndBroadcastOverlays: LeagueCompetitionEventDisplay[];
}

export function orderHomeCompetitions(
  competitions: LeagueCompetitionEventDisplay[],
  relativeTo: Date = new Date(),
): LeagueCompetitionEventDisplay[] {
  return orderByCompetitionRelevance(competitions, (competition) => competition, relativeTo);
}

export function selectHomeCompetitionRows(
  competitions: LeagueCompetitionEventDisplay[],
  relativeTo: Date = new Date(),
): HomeCompetitionRows {
  const published = orderHomeCompetitions(
    competitions.filter((competition) => competition.instance.status === "published"),
    relativeTo,
  );

  // Relevance order leads with today's/the next competition when one exists,
  // otherwise with the most recent past one — only the former is "next up".
  const leadOffset = competitionDayOffset(published[0], relativeTo);
  const nextUp: HomeNextUpCompetition | null =
    published[0] && leadOffset !== undefined && leadOffset >= 0
      ? { display: published[0], dayOffset: leadOffset }
      : null;

  const remaining = nextUp ? published.slice(1) : published;
  const isUpcoming = (competition: LeagueCompetitionEventDisplay) =>
    (competitionDayOffset(competition, relativeTo) ?? -1) >= 0;
  const schedule = [
    ...remaining.filter(isUpcoming),
    ...remaining.filter((competition) => !isUpcoming(competition)),
  ].slice(0, SCHEDULE_ROW_LIMIT);

  const gpsAndBroadcastOverlays = orderHomeCompetitions(
    competitions.filter((competition) => competition.isGPSRace),
    relativeTo,
  ).slice(0, GPS_ROW_LIMIT);

  return { nextUp, schedule, gpsAndBroadcastOverlays };
}

/** "Today", "Tomorrow", "In 8 days" — display label for a `HomeNextUpCompetition.dayOffset`. */
export function relativeEventDayLabel(dayOffset: number, locale?: string): string {
  const label = new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(dayOffset, "day");
  return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
}
